import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Edit2, Clock, Users } from 'lucide-react';
import { useI18n, formatDateWithMonthName } from '../i18n';
import CategoryIcon from './CategoryIcon';
import TopStackedChart from './TopStackedChart';
import { useMediaQuery } from '../hooks';
import { getMemberHex, getCategoryName, getCategoryColor, formatarMinutos, parseDateInput } from '../utils';
import { Avatar, Button, Card, EmptyState, Field, IconButton, Input, Label, Notice, PageHeader, SectionTitle, Segmented, Select } from './ui';

const ITEMS_PER_PAGE = 50;

export default function Dashboard({ data, projeto, perfil, onNewEntry, onEditEntry, onDeleteEntry, onNavigate }) {
  const { t, lang } = useI18n();
  const wide = useMediaQuery('(min-width: 768px)');
  const [periodoDeTempo, setPeriodoDeTempo] = useState('7');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  const tarefaById = useMemo(() => new Map(data.tarefas.map(tf => [tf.id, tf])), [data.tarefas]);
  const membroById = useMemo(() => new Map(data.membros.map(m => [m.id, m])), [data.membros]);
  const categoriaById = useMemo(() => new Map(data.categorias.map(c => [c.id, c])), [data.categorias]);

  const saldos = {};
  data.membros.forEach(m => { saldos[m.id] = 0; });
  data.registros.forEach(reg => {
    const tarefa = tarefaById.get(reg.tarefa_id);
    if (tarefa && reg.membro_id in saldos) saldos[reg.membro_id] += (reg.tempo_minutos || 0) * tarefa.peso;
  });
  const tarefasPorMembro = data.registros.reduce((acc, r) => {
    acc[r.membro_id] = (acc[r.membro_id] || 0) + 1;
    return acc;
  }, {});
  const membrosComSaldo = data.membros
    .map(m => ({ ...m, saldo: saldos[m.id] || 0, totalTarefas: tarefasPorMembro[m.id] || 0 }))
    .sort((a, b) => b.saldo - a.saldo);
  const lider = membrosComSaldo[0];
  const maiorSaldo = lider?.saldo || 0;
  const membrosComDivida = membrosComSaldo.map(m => ({ ...m, divida: maiorSaldo - m.saldo })).filter(m => m.divida > 0);

  const filtrarPorPeriodo = (registros, dias) => {
    if (dias === 'all') return registros;
    const hoje = new Date();
    let dataLimite;
    if (dias === 'ytd') {
      dataLimite = new Date(hoje.getFullYear(), 0, 1);
    } else {
      dataLimite = new Date(hoje);
      dataLimite.setDate(hoje.getDate() - parseInt(dias, 10));
    }
    return registros.filter(registro => new Date(registro.criado_em) >= dataLimite);
  };

  const registrosDoPeriodo = filtrarPorPeriodo(data.registros, periodoDeTempo);
  const totalTarefas = registrosDoPeriodo.length;
  const totalMinutosPeriodo = registrosDoPeriodo.reduce((acc, reg) => {
    const tarefa = tarefaById.get(reg.tarefa_id);
    return acc + (reg.tempo_minutos || 0) * (tarefa?.peso || 0);
  }, 0);

  const dataInicio = filtroDataInicio ? parseDateInput(filtroDataInicio) : null;
  const dataFim = filtroDataFim ? parseDateInput(filtroDataFim, true) : null;
  const registrosFiltrados = data.registros.filter(registro => {
    const tarefa = tarefaById.get(registro.tarefa_id);
    const dataRegistro = new Date(registro.criado_em);
    if (filtroCategoria !== 'todas' && tarefa?.categoria_id !== parseInt(filtroCategoria, 10)) return false;
    if (dataInicio && dataRegistro < dataInicio) return false;
    if (dataFim && dataRegistro > dataFim) return false;
    return true;
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [filtroCategoria, filtroDataInicio, filtroDataFim]);

  const totalPages = Math.ceil(registrosFiltrados.length / ITEMS_PER_PAGE);
  // Deleting the last entry of the last page would otherwise leave an empty page.
  const page = Math.min(currentPage, Math.max(0, totalPages - 1));
  const startIndex = page * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const registrosPaginados = registrosFiltrados.slice(startIndex, endIndex);
  const filtrosAtivos = filtroCategoria !== 'todas' || filtroDataInicio || filtroDataFim;

  const metadadosIncompletos = !projeto?.finalidade || !projeto?.tem_filhos || !projeto?.tem_animais;

  const periodos = [
    ['7', t('dashboard.period7')],
    ['30', t('dashboard.period30')],
    ['90', t('dashboard.period90')],
    ['180', t('dashboard.period180')],
    ['ytd', t('dashboard.periodYtd')],
    ['all', t('dashboard.periodAll')],
  ];
  const periodoLabel = periodos.find(([v]) => v === periodoDeTempo)?.[1];

  const primeiroNome = (perfil?.nome || '').trim().split(/\s+/)[0];
  const title = primeiroNome ? t('dashboard.greeting', { name: primeiroNome }) : t('dashboard.title');
  const subtitle = projeto?.nome ? t('dashboard.subtitle', { project: projeto.nome }) : null;

  const entryParts = (registro) => {
    const membro = membroById.get(registro.membro_id);
    const tarefa = tarefaById.get(registro.tarefa_id);
    const categoria = categoriaById.get(tarefa?.categoria_id);
    return {
      membro,
      tarefa,
      categoria,
      hex: getMemberHex(registro.membro_id, data.membros),
      dataLabel: formatDateWithMonthName(new Date(registro.criado_em), lang),
      calculo: `${formatarMinutos(registro.tempo_minutos)} × ${tarefa?.peso ?? '—'}`,
      total: formatarMinutos((registro.tempo_minutos || 0) * (tarefa?.peso || 0)),
    };
  };

  const actions = (registro) => (
    <>
      <IconButton icon={Edit2} label={t('common.edit')} onClick={() => onEditEntry(registro)} />
      <IconButton icon={Trash2} label={t('common.delete')} tone="danger" onClick={() => onDeleteEntry(registro)} />
    </>
  );

  const renderTable = () => (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left">
          {[t('dashboard.colParticipant'), t('dashboard.colTask'), t('dashboard.colDate'), t('dashboard.colTime'), t('dashboard.colTotal'), ''].map((label, i) => (
            <th key={i} className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 ${i === 4 ? 'text-right' : ''}`}>{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {registrosPaginados.map(registro => {
          const e = entryParts(registro);
          return (
            <tr key={registro.id} data-entry className="border-t border-gray-100 hover:bg-gray-50/70">
              <td className="px-4 py-3 align-top">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Avatar src={e.membro?.avatar_url} name={e.membro?.nome} color={e.hex} size="xs" />
                  <span className="font-medium text-gray-900 truncate">{e.membro?.nome}</span>
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className="flex items-start gap-2 min-w-0">
                  {e.categoria && (
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0 ${getCategoryColor(e.categoria.id)}`} title={getCategoryName(e.categoria, t)}>
                      <CategoryIcon iconName={e.categoria.icone} size={13} />
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-gray-900">{e.tarefa?.nome}</div>
                    {registro.comentario && <div className="text-xs text-gray-500 whitespace-pre-wrap break-words mt-0.5">{registro.comentario}</div>}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 align-top text-gray-500 tabular-nums whitespace-nowrap">{e.dataLabel}</td>
              <td className="px-4 py-3 align-top text-gray-500 tabular-nums whitespace-nowrap">{e.calculo}</td>
              <td className="px-4 py-3 align-top text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">{e.total}</td>
              <td className="px-2 py-2 align-top text-right whitespace-nowrap">{actions(registro)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderCards = () => (
    <div className="divide-y divide-gray-100">
      {registrosPaginados.map(registro => {
        const e = entryParts(registro);
        return (
          <div key={registro.id} data-entry className="p-3 flex items-start gap-3">
            <Avatar src={e.membro?.avatar_url} name={e.membro?.nome} color={e.hex} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{e.tarefa?.nome}</div>
                  <div className="text-xs text-gray-500 tabular-nums">{e.membro?.nome} · {e.dataLabel}</div>
                </div>
                <div className="font-semibold text-gray-900 tabular-nums text-sm whitespace-nowrap">{e.total}</div>
              </div>
              <div className="text-xs text-gray-400 tabular-nums mt-0.5">{e.calculo}</div>
              {registro.comentario && <p className="text-sm text-gray-600 mt-1.5 whitespace-pre-wrap break-words">{registro.comentario}</p>}
            </div>
            <div className="flex flex-col -mr-1 -mt-1">{actions(registro)}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={<Button onClick={onNewEntry}><Plus size={16} />{t('dashboard.registerActivity')}</Button>}
      />

      {metadadosIncompletos && (
        <Notice tone="warning" className="mb-6" action={<Button size="sm" variant="secondary" onClick={() => onNavigate('config')}>{t('dashboard.metadataWarningCta')}</Button>}>
          <span className="font-medium">{t('dashboard.metadataWarningTitle')}</span>
          <span className="block text-gray-600">{t('dashboard.metadataWarningBody')}</span>
        </Notice>
      )}

      {data.membros.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('dashboard.noMembers')}
          action={<Button onClick={() => onNavigate('membros')}><Plus size={16} />{t('dashboard.addFirstMember')}</Button>}
        />
      ) : (
        <>
          <section className="mb-8">
            <SectionTitle actions={<Segmented value={periodoDeTempo} onChange={setPeriodoDeTempo} options={periodos} />}>
              {t('dashboard.overviewTitle')}
            </SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="p-4">
                <Label>{t('dashboard.statTasks')}</Label>
                <div className="text-2xl font-semibold text-gray-900 tabular-nums mt-1">{totalTarefas}</div>
                <div className="text-xs text-gray-500 mt-1">{periodoLabel}</div>
              </Card>
              <Card className="p-4">
                <Label>{t('dashboard.statTime')}</Label>
                <div className="text-2xl font-semibold text-gray-900 tabular-nums mt-1">{formatarMinutos(totalMinutosPeriodo)}</div>
                <div className="text-xs text-gray-500 mt-1">{t('dashboard.totalTimeLabel')}</div>
              </Card>
              <Card className="p-4">
                <Label>{t('dashboard.statLeader')}</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  {lider && <Avatar src={lider.avatar_url} name={lider.nome} color={getMemberHex(lider.id, data.membros)} size="sm" />}
                  <div className="text-lg font-semibold text-gray-900 truncate">{lider?.nome || '—'}</div>
                </div>
                <div className="text-xs text-gray-500 mt-1 tabular-nums">{formatarMinutos(maiorSaldo)} · {t('dashboard.allTime')}</div>
              </Card>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            <Card className="p-4 sm:p-5">
              <SectionTitle>{t('dashboard.appliedTimeTitle')}</SectionTitle>
              <div className="space-y-3">
                {membrosComSaldo.map((membro) => {
                  const hex = getMemberHex(membro.id, data.membros);
                  const pct = maiorSaldo ? Math.round((membro.saldo / maiorSaldo) * 100) : 0;
                  return (
                    <div key={membro.id} className="flex items-center gap-3">
                      <Avatar src={membro.avatar_url} name={membro.nome} color={hex} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium text-gray-900 truncate">{membro.nome}</span>
                          <span className="text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap">{formatarMinutos(membro.saldo)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: hex }} />
                          </div>
                          <span className="text-xs text-gray-400 tabular-nums whitespace-nowrap">{t('dashboard.tasksCount', { count: membro.totalTarefas })}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="p-4 sm:p-5">
              <SectionTitle>{t('dashboard.balanceTitle')}</SectionTitle>
              {data.membros.length >= 2 && membrosComDivida.length > 0 ? (
                <div className="space-y-2">
                  {membrosComDivida.map(devedor => (
                    <div key={devedor.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar src={devedor.avatar_url} name={devedor.nome} color={getMemberHex(devedor.id, data.membros)} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{devedor.nome}</div>
                        <div className="text-xs text-gray-500">{t('dashboard.needsToCompensate')}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900 tabular-nums">{formatarMinutos(devedor.divida)}</div>
                        <div className="text-xs text-gray-500">{t('dashboard.catchUpTo', { name: lider?.nome || '' })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-6 text-center">{t('dashboard.balanced')}</p>
              )}
            </Card>
          </section>

          {data.registros.length > 0 && (
            <section className="mb-8">
              <TopStackedChart registros={data.registros} tarefas={data.tarefas} categorias={data.categorias} membros={data.membros} />
            </section>
          )}
        </>
      )}

      <section>
        <SectionTitle>{t('dashboard.historyTitle')}</SectionTitle>
        <Card>
          <div className="flex flex-col md:flex-row md:items-end gap-3 p-4 border-b border-gray-100">
            <Field label={t('dashboard.filterCategory')} htmlFor="filtro-categoria" className="md:w-56">
              <Select id="filtro-categoria" value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
                <option value="todas">{t('dashboard.filterAllCategories')}</option>
                {data.categorias.map(cat => (<option key={cat.id} value={cat.id}>{getCategoryName(cat, t)}</option>))}
              </Select>
            </Field>
            <Field label={t('dashboard.filterStartDate')} htmlFor="filtro-data-inicio" className="md:w-44">
              <Input id="filtro-data-inicio" type="date" value={filtroDataInicio} onChange={(e) => setFiltroDataInicio(e.target.value)} />
            </Field>
            <Field label={t('dashboard.filterEndDate')} htmlFor="filtro-data-fim" className="md:w-44">
              <Input id="filtro-data-fim" type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} />
            </Field>
            {filtrosAtivos && (
              <Button variant="ghost" size="sm" className="self-start md:self-auto md:mb-0.5" onClick={() => { setFiltroCategoria('todas'); setFiltroDataInicio(''); setFiltroDataFim(''); }}>
                {t('dashboard.clearFilters')}
              </Button>
            )}
          </div>

          {registrosFiltrados.length === 0 ? (
            <div className="p-4"><EmptyState icon={Clock} title={t('dashboard.noActivities')} /></div>
          ) : (
            <div className={wide ? 'overflow-x-auto' : ''}>{wide ? renderTable() : renderCards()}</div>
          )}

          {registrosFiltrados.length > 0 && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 tabular-nums">
                {t('dashboard.paginationInfo', { start: startIndex + 1, end: Math.min(endIndex, registrosFiltrados.length), total: registrosFiltrados.length })}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="secondary" size="sm" onClick={() => setCurrentPage(Math.max(0, page - 1))} disabled={page === 0}>{t('dashboard.previous')}</Button>
                <div className="hidden sm:flex items-center gap-1 mx-1">
                  {Array.from({ length: totalPages }, (_, i) => i).map(pageNum => (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      aria-current={page === pageNum ? 'page' : undefined}
                      className={`w-8 h-8 rounded-md text-xs font-medium tabular-nums transition-colors ${page === pageNum ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      {pageNum + 1}
                    </button>
                  ))}
                </div>
                <Button variant="secondary" size="sm" onClick={() => setCurrentPage(Math.min(totalPages - 1, page + 1))} disabled={page === totalPages - 1}>{t('dashboard.next')}</Button>
              </div>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
